import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Users, Home, Briefcase, Newspaper, GraduationCap, BookOpen, Wrench,
    Rocket, TrendingUp, HeartPulse, Scale, Landmark, Stethoscope, ChevronDown,
} from "lucide-react";

/**
 * SectorsMegaMenu — 13-item hover/click dropdown for the landing nav.
 * Sprint D Addendum #1.
 */
const SECTORS = [
    { slug: "hr",                 label: "HR",                 Icon: Users },
    { slug: "real-estate",        label: "Real Estate",        Icon: Home },
    { slug: "consulting",         label: "Consulting",         Icon: Briefcase },
    { slug: "journalism",         label: "Journalism",         Icon: Newspaper },
    { slug: "students",           label: "Students",           Icon: GraduationCap },
    { slug: "education",          label: "Education",          Icon: BookOpen },
    { slug: "restoration",        label: "Restoration",        Icon: Wrench },
    { slug: "startup",            label: "Start-up",           Icon: Rocket },
    { slug: "sales",              label: "Sales",              Icon: TrendingUp },
    { slug: "health",             label: "Health",             Icon: HeartPulse },
    { slug: "legal",              label: "Legal",              Icon: Scale,       badge: "New — Q1" },
    { slug: "government-affairs", label: "Government Affairs", Icon: Landmark,    badge: "New — Q1" },
    { slug: "healthcare-admin",   label: "Healthcare Admin",   Icon: Stethoscope },
];

export default function SectorsMegaMenu() {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        function onDocClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        function onEsc(e) { if (e.key === "Escape") setOpen(false); }
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onEsc);
        };
    }, []);

    return (
        <div
            ref={ref}
            className="relative"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid="sectors-menu-trigger"
                className="inline-flex items-center gap-1 text-[13.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--atlas-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a1a] rounded px-1 py-1"
                style={{ color: open ? "var(--atlas-primary-hover)" : "var(--atlas-text-primary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--atlas-primary-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = open ? "var(--atlas-primary-hover)" : "var(--atlas-text-primary)")}
            >
                Sectors <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
            </button>

            {open && (
                <div
                    data-testid="sectors-menu-panel"
                    className="absolute right-0 sm:left-1/2 sm:-translate-x-1/2 mt-2 w-[560px] max-w-[calc(100vw-32px)] rounded-2xl border border-[#e8e8e3] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_20px_60px_-20px_rgba(15,23,42,0.2)] overflow-hidden z-50"
                >
                    <div className="p-3 grid grid-cols-2 gap-1.5">
                        {SECTORS.map(({ slug, label, Icon, badge }) => (
                            <Link
                                key={slug}
                                to={`/sectors/${slug}`}
                                onClick={() => setOpen(false)}
                                data-testid={`sector-item-${slug}`}
                                className="group inline-flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-[#f0fdf4] transition-colors"
                            >
                                <span className="w-8 h-8 rounded-md bg-[#fafaf7] group-hover:bg-white grid place-items-center flex-shrink-0 transition-colors">
                                    <Icon className="w-4 h-4 text-gray-500 group-hover:text-[#16a34a]" strokeWidth={1.75} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[13px] font-medium text-gray-900 truncate flex items-center gap-1.5">
                                        {label}
                                        {badge && (
                                            <span className="text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#dcfce7] text-[#166534]">
                                                {badge}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                    <div className="border-t border-[#e8e8e3] bg-[#fafaf7] px-4 py-2.5 text-[11px] text-gray-500">
                        Verticals coming across Phase 4 & 5 · <Link to="/sectors" className="text-[#16a34a] font-medium">All sectors →</Link>
                    </div>
                </div>
            )}
        </div>
    );
}

export { SECTORS };
